import { Log, UserManagerSettings, WebStorageStateStore } from "oidc-client-ts";
import { get as getCookie, makeCookieOptions } from "../utils/SafeCookie";
import CountryUtil from "../utils/CountryUtil";
import RequestUtil from "../utils/RequestUtil";
import Tracking from "../tracking/Tracking";
import { Store } from "../utils/Store";
import { LogoutActions } from "../actions/LogoutAction";
import { RefreshAction } from "../actions/RefreshAction";
import { ClientSession } from "../utils/ClientSessionHolder";
import { LoginStoreActions } from "../actions/LoginStoreActions";
import GdprStorage from "../storage/GdprStorage";
import { renderLogoutIframe } from "../utils/IdamLogoutUtil";
import LocaleUtil from "../utils/LocaleUtil";
import { BettyRedirectNavigator } from "../utils/BettyRedirectNavigator";
import type { LoginStateType } from "./SessionState";
import SessionState from "./SessionState";
import LoginUtil, { IdamUserManager } from "../utils/LoginUtil";
import { REQUEST_PRIORITY } from "../utils/RequestPriorityUtil";
import { ApiUrlUtil } from "../utils/ApiUrlUtil";
import { TrackingLogger } from "../utils/TrackingLogger";
import * as BettyJwt from "../../model/BettyJwt";
import { jwtDecode } from "jwt-decode";
import history from "../betty-history";
import JWTChecker from "./JWTChecker";
import { fetchWithTimeoutSyncResponse, SyncResponse } from "../../utils/utilFunctions";

type RedirectState = {
	checkNext: boolean;
	didNavigate: boolean;
	didRedirect: boolean;
};

/** Result of a hook that basically says the hook did nothing */
const HOOK_SKIPPED = {
	checkNext: true,
	didNavigate: false,
	didRedirect: false,
};

/** Result of a hook that navigates via window.location */
const HOOK_NAVIGATED = {
	checkNext: false,
	didNavigate: true,
	didRedirect: false,
};

/** Result of a hook that redirects via react-router */
const HOOK_REDIRECTED = {
	checkNext: false,
	didNavigate: false,
	didRedirect: true,
};

const LOGIN_PARKING_PATH = "/authLoading";
type LoginHandler = {
	transitionTo: (...args: Array<any>) => any;
};
type IdamError =
	| {
			code: string;
			responseText?: string;
			statusCode?: number;
	  }
	| null
	| undefined;

class LoginStoreImpl extends Store {
	_idamLoginBaseUrl = "";
	_waitingForIdamLogout = false;
	_failedStatusCode?: number;
	_failReason?: string;
	_sessionWatcher?: Record<string, any>;
	_userManager?: Record<string, any>;
	_idamError: IdamError;
	SessionStore: any;
	_refreshCompressedJWT: string | null | undefined;
	_extraQueryParams: {
		realm_id: string;
		country_code: string;
		locale_id: string;
		user_type?: string;
		account_id?: string;
	} = {} as any;
	_user: any;
	_idamSingleLogOutTimeoutId: number | null | undefined;
	_refreshId: number | null | undefined;
	_checkCookieId: number | null | undefined;
	_checkCookieTimeout: number | null | undefined;

	constructor() {
		super();
		SessionState.setLoginState("initial"); // either "initial", "pending", "loggedin", "timedOut", or "failed"

		this.listenTo(LogoutActions.Logout, this._doIdamLogoutIfPossible);
		this.listenTo(LogoutActions.LogoutManual, this._doIdamLogoutIfPossible);
		this.listenTo(RefreshAction, this._refreshJwt);
		this._user = undefined;
		this._idamError = null;

		// Log OIDC client debug logs to console for investigating SDCORP-527573
		if (window.datadogEnvironment && window.datadogEnvironment.includes("gcw-pp")) {
			//@ts-expect-error: Log type does not have logger, this is probably buggy
			Log.logger = console;
			//@ts-expect-error: Log type does not have level, this is probably buggy
			Log.level = Log.DEBUG;
		} else {
			//@ts-expect-error: Log type does not have logger, this is probably buggy
			Log.logger = TrackingLogger;
		}
	}

	// I know this is super dirty... but I have no idea atm how to completely extract the needed information from the SessionStore
	// e.g. the customer id. Furthermore this results in our actions to be needed to called with .trigger(), because
	// otherwise we don't get the most recent status of the SessionStore... e.g. isEmployee() will return false
	// even though we just logged in successfully as an employee
	init(sessionStore: Store): void {
		const optionalCountryConfig: Record<string, any> = CountryUtil.getOptionalConfig();

		if (window.location.hostname.endsWith("metrosystems.net")) {
			this._idamLoginBaseUrl = optionalCountryConfig?.["idam-config"]?.default_url ?? window.idam_login_base_url;
		} else if (LoginUtil.isIdamEmployee()) {
			this._idamLoginBaseUrl = optionalCountryConfig?.["idam-employee-config"]?.url ?? window.idam_login_base_url;
		} else {
			this._idamLoginBaseUrl = optionalCountryConfig?.["idam-config"]?.url ?? window.idam_login_base_url;
		}

		this._waitingForIdamLogout = false;
		this._extraQueryParams = {
			realm_id: optionalCountryConfig?.["idam-config"]?.realm ?? "BETTY_REALM",
			country_code: CountryUtil.getCountry(),
			locale_id: LocaleUtil.getLocale(),
		};
		this._idamSingleLogOutTimeoutId = null;
		// workaround for localhost auto logout problem: skip oidc iframe oauth2 session managment
		const isMonitorSession = !window.location.hostname.endsWith("localhost");

		const settings: UserManagerSettings = {
			authority: this._idamLoginBaseUrl!,
			client_id: "BTEX",
			scope: "openid clnt=BTEX",
			response_type: "code",
			monitorSession: isMonitorSession,
			loadUserInfo: true,
			//@ts-expect-error: revokeAccessTokenOnSignout does not in type definition
			revokeAccessTokenOnSignout: true,
			silentRequestTimeoutInSeconds: 15,
			// sec
			automaticSilentRenew: true,
			silent_redirect_uri: window.location.origin + "/ordercapture/uidispatcher/static/silent-redirect.html",
			extraQueryParams: this._extraQueryParams,
			redirectNavigator: BettyRedirectNavigator,
			userStore: new WebStorageStateStore(),
		};
		this._refreshCompressedJWT = undefined;
		this.SessionStore = sessionStore;

		if (this.shouldUseIdamLogin()) {
			this._userManager = new IdamUserManager(settings);

			// register events
			this._userManager.events.addUserSessionChanged(user => {
				this._user = user;
				const userType = LoginUtil.getUserTypeFromAccessToken(user.access_token);
				GdprStorage.cookie.put("USER_TYPE", userType, GdprStorage.NECESSARY, makeCookieOptions());
				this.storeTokensIfPresentAndStartSilentRenewOnsuccess(user);

				this._exchangeTokenForJwt(user.access_token);

				Tracking.trackError(
					"platform",
					"ui",
					"LoginStore",
					`addUserSessionChanged, user ${JSON.stringify(
						this.stripTokensFromUser(user, "addUserSessionChanged"),
					)}`,
				);
			});

			// signout stuff
			this._userManager.events.addUserUnloaded(user => {
				this._user = undefined;

				if (this.getState() === "loggedin") {
					Tracking.trackError(
						"platform",
						"ui",
						"LoginStore",
						`addUserUnloaded and logout, user ${JSON.stringify(
							this.stripTokensFromUser(user, "addUserUnloaded"),
						)}`,
					);

					this._doLogout(undefined, false);
				}
			});

			this._userManager.events.addUserLoaded(user => {
				this._user = user;
				const userType = LoginUtil.getUserTypeFromAccessToken(user.access_token);
				this.storeTokensIfPresentAndStartSilentRenewOnsuccess(user);
				GdprStorage.cookie.put("USER_TYPE", userType, GdprStorage.NECESSARY, makeCookieOptions());

				if (userType === "EMP") {
					const employeeProfile = this._getEmployeeProfile(user.profile);

					GdprStorage.localStorage.put("employee_profile", employeeProfile, GdprStorage.NECESSARY);
				}

				Tracking.trackError(
					"platform",
					"ui",
					"LoginStore",
					`addUserLoaded, user ${JSON.stringify(this.stripTokensFromUser(user, "addUserLoaded"))}`,
				);
			});

			this._userManager.events.addUserSignedOut(user => {
				this._user = undefined;

				// we were waiting for this logout, since we triggered it
				if (this._idamSingleLogOutTimeoutId) {
					window.clearTimeout(this._idamSingleLogOutTimeoutId);
				}

				if (this.getState() === "loggedin") {
					this._doLogout(undefined, false);

					Tracking.trackError(
						"platform",
						"ui",
						"LoginStore",
						`addUserSignedOut and logout, user ${JSON.stringify(
							this.stripTokensFromUser(user, "addUserSignedOut"),
						)}`,
					);
				}
			});

			this._userManager.events.addSilentRenewError(err => {
				this.trackIntoDana(err, "addSilentRenewError");
				this._user = undefined;

				this._doLogout(undefined, true);
			});
		}
	}

	stripTokensFromUser(user: Record<string, any>, eventName: string) {
		try {
			const clonedUser = structuredClone(user);
			clonedUser.access_token = "stripped";
			clonedUser.id_token = "stripped";
			return clonedUser;
		} catch (e) {
			console.error(`could not clone user, oidc event ${eventName} will not be tracked. exception: ${e}`);
		}

		return null;
	}

	storeTokensIfPresentAndStartSilentRenewOnsuccess(user: Record<string, any>) {
		const tokensSuccessfullyStored = LoginUtil.checkUserAndStoreTokens(user);

		if (tokensSuccessfullyStored) {
			this._userManager?.startSilentRenew();
		}
	}

	_isTier1Tier2LoginFlowEnabled(): boolean {
		return window.alternativeJwtPersistFlowEnabled === true || window.alternativeJwtPersistFlowEnabled === "true";
	}

	jwtNeedsToBePersisted(jwt: Record<string, any>): boolean {
		return (
			!this._isTier1Tier2LoginFlowEnabled() ||
			BettyJwt.isTier3(jwt) ||
			BettyJwt.isTier4(jwt) ||
			BettyJwt.isEmployee(jwt)
		);
	}

	requiresCustomerTier3Tier4Flow(): boolean {
		const customerTierLevel = GdprStorage.localStorage.read("customer_tier_level");
		return !this._isTier1Tier2LoginFlowEnabled() || customerTierLevel === "3" || customerTierLevel === "4";
	}

	isIdamTokenExpired(token: string): boolean {
		const decodedToken: Record<string, any> = jwtDecode(token);
		const expiresAt = decodedToken["exp"] as number;
		const now = Date.now() / 1000;
		return now >= expiresAt;
	}

	_getEmployeeProfile(profile: Record<string, any>): string {
		let userProfile;

		try {
			userProfile = {
				fullName: profile.name,
				firstName: profile.given_name,
				lastName: profile.family_name,
				email: profile.email,
				upn: profile.upn,
			};
			userProfile = JSON.stringify(userProfile);
		} catch (error: any) {
			Tracking.trackError(
				"platform",
				"ui",
				"LoginStore",
				"could not get employee profile from IDAM response. '" + JSON.stringify(profile) + "'",
				error.stack || error,
			);
			console.error(
				"could not get employee profile from IDAM response. '" + JSON.stringify(profile) + "'",
				error.stack || error,
			);
		}

		return userProfile;
	}

	async start() {
		const state = history.get().getCurrentLocation();
		const params = LoginUtil.getOAuthRedirectParams(state);
		let redirectUri;
		let realm;

		// we came from idam, check for error and get redirect uri
		if (params.isIdamRedirect) {
			this._setState("pending");

			let userType;

			try {
				const user = await this._userManager?.signinCallback();
				userType = LoginUtil.getUserTypeFromAccessToken(user.access_token);
				realm = LoginUtil.getRealmFromIdToken(user.id_token);
				LoginUtil.storeTokens(user.access_token, user.id_token);
			} catch (error: any) {
				Tracking.trackError(
					"platform",
					"ui",
					"LoginStore",
					"could not decode tokens in signin response of previous user.",
					error.stack || error,
				);
				console.error("could not decode tokens in signin response of previous user.", error.stack || error);
			}

			if (userType) {
				const date = new Date();
				date.setMonth(date.getMonth() + 1);
				GdprStorage.cookie.put("USER_TYPE", userType, GdprStorage.NECESSARY, makeCookieOptions());
			}

			history.get().push(LOGIN_PARKING_PATH);
			GdprStorage.cookie.put("INTERNAL_LOGIN_DISABLE_SSO", "false", GdprStorage.FUNCTIONAL, makeCookieOptions());

			this._clearJwt();

			if (params.error) {
				Tracking.trackError("platform", "ui", "LoginStore", "Failed to login through idam.", params);
				console.error("Failed to login through idam. " + params.error + ": " + params.errorDescription);

				this._setState("failed");

				return;
			}

			redirectUri = LoginUtil.removeIdamRedirectParam(params.redirectUri);
		}

		let jwtHasJustBeenSet = false;
		if (
			!this.loginWasInternal() &&
			this.shouldUseIdamLogin() &&
			!history.pathname?.startsWith("/logout") &&
			!this.loggedInViaBearerToken()
		) {
			try {
				this._setState("pending");

				const redirectParams = {
					extraQueryParams: this._extraQueryParams,
				};
				const userType = GdprStorage.cookie.read("USER_TYPE");

				if (userType) {
					redirectParams.extraQueryParams["user_type"] = userType;
				}

				if (realm) {
					redirectParams.extraQueryParams["realm_id"] = realm;
				}

				const idamUserAccessToken = GdprStorage.localStorage.read("idamUserAccessToken");

				if (idamUserAccessToken && !this.isIdamTokenExpired(idamUserAccessToken)) {
					await this._exchangeTokenForJwt(idamUserAccessToken, redirectUri);
				} else {
					console.info("No token present in localstorage trying signinSilent");
					const user = await this._userManager?.signinSilent(redirectParams);
					this.storeTokensIfPresentAndStartSilentRenewOnsuccess(user);
					await this._exchangeTokenForJwt(user.access_token, redirectUri);
				}
				jwtHasJustBeenSet = true;
			} catch (error: any) {
				this.trackIntoDana(error, "in start method");
				console.warn("silent auth failed with error ", error);
				// just clear the (possibly stale) jwt and do not do any redirects.
				// If we came from idam, we are on the parking page, an error is displayed, and the user can logout.
				// If it was an anonymous (i.e. not logged in) customer, we will show the anonymous shop if possible
				await this._doLogout("notLoggedin", true);
			}
		}

		if (this.requiresCustomerTier3Tier4Flow()) {
			if (!jwtHasJustBeenSet) {
				this._refreshJwt(true);
			}

			this._refreshJwtRepeatedly();

			this._checkJwtCookieRepeatedly();
		}
	}

	trackIntoDana(error: Error, method: string) {
		const idamTokenFromStorage = GdprStorage.cookie.read("idamUserAccessToken");

		if (idamTokenFromStorage) {
			const email = LoginUtil.getUserEmailFromAccessToken(idamTokenFromStorage);
			const metroId = LoginUtil.getMetroIdFromAccessToken(idamTokenFromStorage);
			const expiresAt = LoginUtil.getExpireTimestampFromAccessToken(idamTokenFromStorage);
			Tracking.trackError(
				"platform",
				"ui",
				"LoginStore",
				"[" +
					method +
					"] signinSilent failed with an error for user " +
					email +
					" with metroId " +
					metroId +
					" and former token expires at " +
					expiresAt,
				{
					wrappedError: error && (error.stack || error),
				},
			);
		} else {
			Tracking.trackError(
				"platform",
				"ui",
				"LoginStore",
				`${method} signinSilent failed with an error, current client timestamp is ${Date.now()}`,
				{
					wrappedError: error && (error.stack || error),
				},
			);
		}
	}

	loggedInViaBearerToken(): boolean {
		return window.loggedInViaBearerToken === true;
	}

	loginHook: (...args: Array<any>) => any = (state, sessionStore, handler): RedirectState => {
		// user is logged in
		if (sessionStore.loggedIn()) {
			// caution: this might redirect to login if need be
			return this._doRedirectForLoggedInUser(state, handler);
		}

		// user is about to be logged in
		if (sessionStore.pending() || state.path?.startsWith(LOGIN_PARKING_PATH)) {
			return HOOK_SKIPPED;
		}

		// user explicitly wants to be logged in
		if (this._isLoginPath(state.path)) {
			return this._doLogin(state, handler);
		}

		// Mario routes that don't need login
		const isFsd = history.getShopFlavor() === "fsd";
		const perDomainConfig: Record<string, any> = CountryUtil.getPerDomainConfig();
		const requireLogin = perDomainConfig && perDomainConfig["requireLogin"];
		const isVisibleForAnonymous =
			sessionStore.isMarioCountry() &&
			state.routeParams &&
			state.routeParams.visibleForAnonymous &&
			!isFsd &&
			!requireLogin;
		const landingPageIsOpened = state.path === "/";
		const showStrippedLandingPage =
			!isVisibleForAnonymous && landingPageIsOpened && CountryUtil.displayAnonLandingPage();
		// public routes
		const isPublicRoute =
			state.path?.startsWith("/public") || (state.routeParams && state.routeParams.publicVisible);

		if ((isPublicRoute || isVisibleForAnonymous || showStrippedLandingPage) && !sessionStore.failed()) {
			return HOOK_SKIPPED;
		}

		// if in doubt, do default:
		return this._doLogin(state, handler);
	};

	getState(): LoginStateType {
		return SessionState.getState().loginState;
	}

	shutdownForExternalRedirect() {
		this._stopRefreshingJwtRepeatedly();

		JWTChecker.stop();

		this._setState("pending");
	}

	getIdamRedirectUrl(url: string): string {
		return RequestUtil.updateUrlParameter(url, "idamRedirect", "1");
	}

	loginWasInternal(): boolean {
		return getCookie("INTERNAL_LOGIN_DISABLE_SSO") === "true";
	}

	/**
	 * Returns a string indicating the reason for a failed login if the latest login has failed. Undefined otherwise.
	 */
	failReason(): string | null | undefined {
		return this.getState() === "failed" ? this._failReason : undefined;
	}

	/**
	 * Returns status code of failed call, otherwise undefined.
	 */
	failedStatusCode(): number | null | undefined {
		return this.getState() === "failed" ? this._failedStatusCode : undefined;
	}

	_cleanup(): boolean {
		GdprStorage.cookie.remove("iPlanetDirectoryPro");
		GdprStorage.cookie.remove("USER_TYPE");
		GdprStorage.localStorage.remove("customer_tier_level");
		JWTChecker.stop();

		this._clearJwt();

		if (this._refreshId) {
			window.clearInterval(this._refreshId);
		}

		if (this._sessionWatcher) {
			this._sessionWatcher.stopWatching();
		}

		if (this._userManager) {
			this._userManager.removeUser();

			this._userManager.clearStaleState();
		}

		return this.getState() === "loggedin";
	}

	_setState(newState: LoginStateType) {
		SessionState.setLoginState(newState);
		LoginStoreActions.LoginStateChanged.trigger();
	}

	// Utility functions for the hooks (and their redirects)
	_isLoginPath(path: string): boolean {
		const matched = /(\/[^/&?#]*)/.exec(path);
		return !!matched && ["/login", "/idamLogin", "/exlogin"].includes(matched[1]);
	}

	// ----------------------------------------------------------
	// Idam redirect / login stuff
	// ----------------------------------------------------------
	getIdamLoginBaseUrl(): string {
		return this._idamLoginBaseUrl;
	}

	shouldUseIdamLogin(): boolean {
		// Do what the uidispatcher tells us, except if we don't have an idam base URL or it's the fake value currently
		// used in betty.yml & co. A bit hacky, but I don't want to break local development for everyone...
		return !window.useLegacyLogin && !!this._idamLoginBaseUrl && this._idamLoginBaseUrl !== "http://test.test.com";
	}

	getIdamLogoutUrl(): string | null | undefined {
		// TODO(VH): we should get this url from the UserManager!
		if (this._idamLoginBaseUrl && this._idamLoginBaseUrl !== "") {
			return this._idamLoginBaseUrl + "/authorize/api/oauth2/op_session_end";
		}

		return null;
	}

	getWaitingForIdamLogout(): boolean {
		return this._waitingForIdamLogout;
	}

	getIdamError(): IdamError {
		return this._idamError;
	}

	getIdamAccessToken(): string | null | undefined {
		return GdprStorage.localStorage.read("idamUserAccessToken");
	}

	_doLogin(state: Record<string, any>, handler: LoginHandler): RedirectState {
		const useIdamLogin = state.path?.startsWith("/idamLogin") || this.shouldUseIdamLogin();

		if (useIdamLogin) {
			this._doIdamRedirect(state.query);
			return HOOK_NAVIGATED;
		}

		if (!state.path?.startsWith("/exlogin")) {
			// redirect to our login page
			if (state.path?.startsWith("/login") && state.query && state.query.longRedirect) {
				// preserve long redirects from orderfulfillment:
				handler.transitionTo("/exlogin", {
					longRedirect: state.query.longRedirect,
				});
			} else {
				const redirectQuery = RequestUtil.buildQuery(state.query);
				handler.transitionTo(
					"/exlogin/" +
						this._getRedirectParam(state.path + (redirectQuery.length > 0 ? "?" + redirectQuery : "")),
				);
			}

			return HOOK_REDIRECTED;
		}

		// else: is explicit deprecated login. will be routed, so nothing to do
		return HOOK_SKIPPED;
	}

	_doIdamRedirect(queryParams: Record<string, any>): void {
		let returnUrl;

		//We are not coming from IDAM: idamRedirect=1 was not set.
		if (LoginUtil.removeIdamRedirectParam(window.location.href) === window.location.href) {
			returnUrl = this.getIdamRedirectUrl(window.location.href);
		} else {
			// When coming from IDAM, remove all search parameters to have a clean redirect url
			returnUrl = this.getIdamRedirectUrl(window.location.href.replace(/([?#].*)/, ""));
		}

		this.shutdownForExternalRedirect();
		// Alex adds a message param if user registered successfully that is shown on IDAM page.
		const message = queryParams && queryParams["message"];
		const redirectParams: any = {
			redirect_uri: returnUrl,
			extraQueryParams: this._extraQueryParams,
		};

		if (message !== undefined && message !== null) {
			redirectParams.extraQueryParams["message"] = message;
		}

		if (LoginUtil.isIdamEmployee()) {
			redirectParams.extraQueryParams["user_type"] = "EMP";
		}

		this._userManager?.signinRedirect(redirectParams);
	}

	switchBusinessAccount(newAccountId: string) {
		if (this.getState() !== "loggedin" || !this.shouldUseIdamLogin()) {
			console.error("tried to switch business accounts, but you are not logged in or not using IDAM.");
			return;
		}

		const returnUrl = this.getIdamRedirectUrl(window.location.href);
		this.shutdownForExternalRedirect();
		const redirectParams: any = {
			redirect_uri: returnUrl,
			extraQueryParams: this._extraQueryParams,
		};
		const userType = GdprStorage.cookie.get("USER_TYPE").actualResult;

		if (userType) {
			redirectParams.extraQueryParams["user_type"] = userType;
		}

		redirectParams.extraQueryParams["account_id"] = newAccountId;

		this._userManager?.signinRedirect(redirectParams);
	}

	/**
	 * Get redirect param from path before doing deprecated explore login.
	 */
	_getRedirectParam(path: string): string {
		if (typeof path === "string" && path.length > 0) {
			if (path.charAt(0) === "/") {
				path = path.substr(1);
			}

			if (this.shouldUseIdamLogin()) {
				//IDAM encodes the URI for us, and web app firewalls tend to complain about double encoding.
				return path;
			}

			return encodeURIComponent(path);
		}

		return "";
	}

	_getCustomerIdParamForLoginIfSetInUrl(): string {
		const params = new URLSearchParams(window.location.search);
		const customerId = params.get("customerIdForBettyLogin");
		return customerId ? "&customerId=" + customerId : "";
	}

	_exchangeTokenForJwt(token: string, redirectUri?: string | null): Record<string, any> {
		this._idamError = null;

		this._setState("pending");

		const successHandler = (response: SyncResponse) => {
			this._doIdamLoginCompleted(response, redirectUri);
		};

		const errorHandler = (response: SyncResponse | null, error: string | null, callTreeId) => {
			if (response == null) {
				Tracking.trackError(
					"platform",
					"ui",
					"LoginStore",
					"Received error for CallTreeId: " + callTreeId + " error:",
					{ error: error },
				);
				return;
			}
			if (response.status !== 403) {
				console.error("idam login failed: ", error, "Response: ", response, "CallTreeId: ", callTreeId);
			}
			// status=0 happens when there is a timeout
			if (response.status >= 500 || response.status === 0) {
				// something went very wrong on the server side, we cannot even attempt to parse the json
				this._idamError = {
					code: "service-error",
					responseText: response.text,
					statusCode: response.status,
				};
				Tracking.trackError(
					"platform",
					"ui",
					"LoginStore",
					"Received idam error from server CallTreeId: " + callTreeId + " error:",
					this._idamError,
				);
			} else {
				// something still went wrong, but probably it's a 403,
				// we should be able to parse the json response
				try {
					this._idamError = JSON.parse(response.text);
					Tracking.trackError(
						"platform",
						"ui",
						"LoginStore",
						"Received idam error from server CallTreeId: " + callTreeId + " error:",
						this._idamError,
					);
				} catch (e: any) {
					this._idamError = {
						code: "parse-error",
					};
					Tracking.trackUIError(
						"platform",
						"ui",
						"LoginStore",
						"Exception while parsing idam error:",
						e.stack || e,
					);
				}
			}

			// reset state on error
			this._clearJwt();

			this._setState("failed");
		};

		const callTreeIdHeader = RequestUtil.makeCallTreeHeaderWithPriority(
			LoginUtil.generateSessionedCallTreeIdWithPrefix("SSto"),
			REQUEST_PRIORITY.HIGH,
		);
		const url =
			"/explore.login.v1/auth/loginWithIdamAccessToken?country=" +
			CountryUtil.getCountry() +
			this._getCustomerIdParamForLoginIfSetInUrl();
		return fetchWithTimeoutSyncResponse(url, {
			method: "post",
			headers: {
				...callTreeIdHeader,
				"Content-Type": "text/plain",
			},
			body: token,
			timeoutInSeconds: 5,
		})
			.then(function (response) {
				if (!response.ok) {
					return errorHandler(response, null, callTreeIdHeader.CallTreeId);
				}
				return successHandler(response);
			})
			.catch(function (error) {
				return errorHandler(null, error, callTreeIdHeader.CallTreeId);
			});
	}

	_doIdamLoginCompleted(response: SyncResponse, redirectUri: string | null | undefined) {
		try {
			if (redirectUri) {
				// removing the idam param that was set to detect IDAM redirects
				const redirectTargetUri = LoginUtil.removeIdamRedirectParam(redirectUri);
				const pathQueryHash = ApiUrlUtil.getPathQueryHash(redirectTargetUri);
				const baseName = history.getBaseName(pathQueryHash);
				let newUrl = pathQueryHash;

				if (newUrl && newUrl.startsWith(baseName)) {
					newUrl = pathQueryHash.replace(baseName, "");
				}
				history.push(newUrl);
			}

			const responseTextAsObject = LoginUtil.parseResponseTextToObject(response.text);
			const rawJwt = responseTextAsObject && responseTextAsObject.JWT;
			const jwt = LoginUtil.parseJWT(rawJwt);
			const compressedJwt = response.headers.get("compressedJWT");

			if (responseTextAsObject && responseTextAsObject.stores && BettyJwt.isEmployee(jwt)) {
				LoginUtil.persistEmployeeStores(responseTextAsObject.stores);
			}

			if (this.jwtNeedsToBePersisted(jwt)) {
				this._persistJwt(rawJwt, compressedJwt);
			} else {
				LoginUtil.removeJwtFromCookieAndStorage();
			}

			LoginUtil.persistCustomerTierLevel(jwt);

			this._onLoginCompleted(jwt);
		} catch (error: any) {
			Tracking.trackError(
				"platform",
				"ui",
				"LoginStore",
				"idam login completion failed with error",
				error.stack || error,
			);
			console.error("idam login completion failed with error ", error.stack || error);
		}
	}

	// ----------------------------------------------------------
	// Non-Idam Login stuff
	// ----------------------------------------------------------
	_onLoginCompleted(jwt: Record<string, any> | null | undefined) {
		try {
			if (this.requiresCustomerTier3Tier4Flow()) {
				BettyJwt.validateJwtToken(jwt);

				this._refreshJwtRepeatedly();
			}

			LoginStoreActions.LoginCompleted.trigger(jwt);

			this._setState("loggedin");
		} catch (e: any) {
			Tracking.trackError("platform", "ui", "LoginStore", "Error with login. Logging out.", e.stack || e);
			console.error("Error with login. Logging out.", e);

			this._doLogout(undefined, false);
		}
	}

	_doRedirectForLoggedInUser(state: Record<string, any>, Handler: Record<string, any>): RedirectState {
		// redirect to '/' for non-employees accessing employee pages
		if (state.routeParams && state.routeParams.needsEmployee && !this.SessionStore.isEmployee()) {
			console.warn("Not an employee -- redirecting to landing page...");
			Handler.transitionTo("/");
			return HOOK_REDIRECTED;
		}

		// long redirect (used by orderfulfillment verticals)
		const longRedirect = state.query && state.query.longRedirect;

		if (longRedirect && longRedirect.match(/^\//) && this.SessionStore.isEmployee()) {
			this.shutdownForExternalRedirect();
			window.location.assign(longRedirect);
			return HOOK_NAVIGATED;
		}

		// redirect away from login pages
		if (this._isLoginPath(state.path) || state.path?.startsWith(LOGIN_PARKING_PATH)) {
			// path based redirect (used by non-of verticals)
			//const pathRedirect = state.path.replace(/^\/[^\/]*/, "");
			const pathRedirect = (state.params && state.params.splat) || "";

			if (
				pathRedirect ||
				!this.SessionStore.isEmployee() ||
				window.without_of_ui_dispatcher === "true" ||
				CountryUtil.isMcrmCountry()
			) {
				Handler.transitionTo("/" + pathRedirect, state.query);
				return HOOK_REDIRECTED;
			} else {
				if (CountryUtil.getOptionalConfig().noOFRedirect) {
					window.location.assign("/backend/orderentry");
				} else {
					this.shutdownForExternalRedirect();
					window.location.assign("/ordermanagement/uidispatcher/static/backend#/employee-start");
				}
				return HOOK_NAVIGATED;
			}
		}

		return HOOK_SKIPPED;
	}

	// ----------------------------------------------------------
	// Jwt related stuff
	// ----------------------------------------------------------
	_refreshJwtRepeatedly() {
		if (this._refreshId) {
			window.clearInterval(this._refreshId);
		}

		this._refreshId = window.setInterval(
			() => {
				this._refreshJwt();
			},
			10 * 60 * 1000,
		); // 10 minutes
	}

	_stopRefreshingJwtRepeatedly() {
		if (this._refreshId) {
			window.clearInterval(this._refreshId);
		}

		this._refreshId = undefined;
	}

	_checkJwtCookieOnce() {
		const jwts = LoginUtil.getJwtFromCookieAndStorage();

		if (this.getState() === "loggedin" && !jwts.compressedJwt) {
			Tracking.trackStructuredEvent("platform", "ui", "technical.logoutFromOtherPage", {});

			this._pushLogoutUrl();
		} else if (this.getState() !== "loggedin" && jwts.compressedJwt && !jwts.jwt) {
			// coming from internal login, for example. Wait for JwtRefresh
			Tracking.trackStructuredEvent("platform", "ui", "technical.loginWithPartialInformation", {});
		} else if (this.getState() !== "loggedin" && jwts.compressedJwt) {
			Tracking.trackStructuredEvent("platform", "ui", "technical.loginFromOtherPage", {});
			const jwt = LoginUtil.parseJWT(jwts.jwt);

			this._onLoginCompleted(jwt);

			if (history.pathname?.startsWith("/logout")) {
				history.push("/");
			}
		}
	}

	_checkJwtCookieRepeatedly() {
		if (this._checkCookieId) {
			window.clearInterval(this._checkCookieId);
		}

		if (this._checkCookieTimeout) {
			window.clearTimeout(this._checkCookieTimeout);
		}

		// check once almost immediately, then every few seconds:
		this._checkCookieTimeout = window.setTimeout(() => {
			this._checkJwtCookieOnce();

			this._checkCookieId = window.setInterval(() => this._checkJwtCookieOnce(), 5 * 1000);
		}, 100);
	}

	/**
	 * Validates the current jwt and triggers the store, if validation is successful
	 */
	_refreshJwt: (...args: Array<any>) => any = isInitialRefresh => {
		const jwts = LoginUtil.getJwtFromCookieAndStorage();

		if (this.requiresCustomerTier3Tier4Flow() && jwts && jwts.compressedJwt) {
			const onRefreshSuccess = (response: SyncResponse) => {
				try {
					const newCompressedJwt = response.headers.get("compressedJWT");

					if (
						typeof this._refreshCompressedJWT === "string" &&
						typeof newCompressedJwt === "string" &&
						this._refreshCompressedJWT === newCompressedJwt
					) {
						return; // nothing to do
					}

					this._refreshCompressedJWT = newCompressedJwt;
					const responseTextAsObject = LoginUtil.parseResponseTextToObject(response.text);
					const newRawJwt = responseTextAsObject && responseTextAsObject.JWT;
					const jwt = LoginUtil.parseJWT(newRawJwt);
					BettyJwt.validateJwtToken(jwt);

					if (this.jwtNeedsToBePersisted(jwt)) {
						this._persistJwt(newRawJwt, newCompressedJwt);
					} else {
						LoginUtil.removeJwtFromCookieAndStorage();
					}

					this._onLoginCompleted(jwt);
				} catch (error: any) {
					Tracking.trackError(
						"platform",
						"ui",
						"LoginStore",
						"refreshJwt failed with error",
						error.stack || error,
					);
					console.error("_refreshJwt failed with error ", error.stack || error);

					this._cleanup();

					this._setState("failed");
				}
			};

			const onRefreshFailed = (response: SyncResponse | null, e: any | null) => {
				const httpStatusCode = response?.status;

				if (isInitialRefresh || httpStatusCode === 403) {
					this._cleanup();

					// do not call "onLogout" here, because that would overwrite the state!
					// cleanUpOnAnyError is set in main.js, calls start and triggers the first call.
					this._setState(isInitialRefresh ? "notLoggedin" : "timedOut");

					console.info("refreshing JWT failed: ", e);

					// For public paths or mario (or login itself) do not redirect to login.
					if (!isInitialRefresh && !history.pathname?.startsWith("/login")) {
						Tracking.trackJwtRefreshEvent(
							"platform",
							"ui",
							"LoginStore",
							`onRefreshFailed 403: ${httpStatusCode ? httpStatusCode : "null"}`,
							e?.stack || e,
						);

						this._pushLogoutUrl();
					}
				} // no else case, because we assume that it will recover e.g. internet timeouts, cosmic rays
			};

			const callTreeIdHeader = RequestUtil.makeCallTreeHeader(
				LoginUtil.generateSessionedCallTreeIdWithPrefix("SSto"),
			);

			return fetchWithTimeoutSyncResponse("/explore.login.v1/auth/singleSignOn", {
				method: "post",
				headers: {
					...callTreeIdHeader,
					"Content-Type": "application/json",
				},
				body: "{}",
				timeoutInSeconds: 30,
			})
				.then(function (response: SyncResponse) {
					if (!response.ok) {
						return onRefreshFailed(response, null);
					}
					return onRefreshSuccess(response);
				})
				.catch(function (error) {
					return onRefreshFailed(null, error);
				});
		}
	};

	_clearJwt() {
		LoginUtil.removeJwtFromCookieAndStorage();
		LoginUtil.removeEmployeeInfoFromCookieAndStorage();
	}

	_persistJwt(jwt: string | null | undefined, compressedJwt: string | null | undefined) {
		const cookieWasSet = GdprStorage.cookie.put(
			"compressedJWT",
			compressedJwt,
			GdprStorage.NECESSARY,
			makeCookieOptions(),
		);

		if (!cookieWasSet) {
			Tracking.trackError("platform", "ui", "LoginStore", "Initial setting of compressedJWT cookie failed.");
			console.error(
				"Initial setting of compressedJWT cookie failed." +
					" Probable cause is too many/too long cookies in this domain." +
					" Try deleting all cookies from this domain, and log in again.",
			);
		}

		GdprStorage.localStorage.put("JWT", jwt, GdprStorage.NECESSARY);

		if (jwt) {
			JWTChecker.start(jwt);
		}
	}

	// ----------------------------------------------------------
	// Idam logout stuff
	// ----------------------------------------------------------
	_doIdamLogoutIfPossible: (...args: Array<any>) => any = () => {
		const idamLogoutUrl = this.getIdamLogoutUrl();
		const waitingForIdamMs = 5000;

		// I know this is weird, but basically to perform a SLO, we should be using IDAM
		if (idamLogoutUrl && this.shouldUseIdamLogin()) {
			this._waitingForIdamLogout = true;
			LoginStoreActions.LoginStateChanged.trigger();
			renderLogoutIframe(idamLogoutUrl);
			this._idamSingleLogOutTimeoutId = window.setTimeout(() => {
				Tracking.trackUIError(
					"platform",
					"ui",
					"LoginStore",
					`Did not receive logout message via idam iframe within the threshold of ${waitingForIdamMs} while communicating with idam iframe:`,
				);
				this._waitingForIdamLogout = false;
				LoginStoreActions.LoginStateChanged.trigger();

				this._doLogout(undefined, false);
			}, waitingForIdamMs);
		} else {
			this._doLogout(undefined, false);
		}
	};

	async _doLogout(newState: any, skipLogoutUrlTransition: boolean) {
		/**
		 * this deletes an old cookie that seems to interfere with the real cookie.
		 * (this happens when we change cookie options like path).
		 */
		const wasLoggedIn = this._cleanup();

		LoginUtil.removeUserTokens();

		this._setState(newState || "logout");

		Tracking.trackLogoutEvent();
		ClientSession.resetClientSessionId();

		this._userManager?.stopSilentRenew();

		try {
			await fetch("/explore.login.v1/auth/logout", {
				method: "post",
				headers: RequestUtil.makeCallTreeHeader(LoginUtil.generateSessionedCallTreeIdWithPrefix("SSto")),
			});
		} catch (error: any) {
			Tracking.trackError("platform", "ui", "LoginStore", "Logout failed", error.stack || error);
			console.error("Logout failed: ", error);
		}

		if (skipLogoutUrlTransition) {
			return;
		}

		if (!history.pathname?.startsWith("/logout")) {
			this._pushLogoutUrl();
		}

		if (wasLoggedIn) {
			window.location.reload();
		}
	}

	_pushLogoutUrl() {
		const pathname = window.location.pathname;
		history.push("/");

		// if we are already on this page then we need to trigger a reload
		// to have any effect:
		if (pathname !== "/") {
			// TODO this should only happen if we are on the same page, not always
			window.location.reload();
		}
	}
}

export default new LoginStoreImpl() as LoginStoreImpl;
